import { Tabs } from '@liveauctioneers/hammer-ui-core/tabs';
import { useCallback } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import CategorySearchCard from '@/components/GridItemCard/Card/CardOptions/CategorySearchCards/CategorySearchCard';
import FluidItemGrid from '@/components/GridItemCard/FluidItemGrid/FluidItemGrid';
import PageTitle from '@/components/PageTitle/PageTitle';
import Pagination from '@/components/Pagination/hammer-pagination/Pagination';
import { PAGE_ITEMS_TO_VIEW } from '@/components/Pagination/helpers/filterSortOptions';
import SaveSearchContainer from '@/components/SaveSearch/SaveSearchContainer';
import { useAppSelector } from '@/redux/hooks';
import { getDiscoverItemIds, getDiscoverPaginationRecordsCount } from '@/redux/modules/discover';
import { getSaveSearchParams } from '@/redux/modules/saveSearch';
import { SearchParams as SearchParamsType } from '@/types/SaveSearch';
import capitalize from '@/utils/capitalize';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';
import { parseQueryParams } from '@/utils/queryParams';
import { safelyRender } from '@/utils/safelyRender';
import { buildSearchQuery } from '@/utils/search/searchQuery';
import { toQueryString } from '@/utils/urls';

import { DiscoverPageHeader } from './DiscoverPageHeader';
import DiscoverPageHeaderMeta from './DiscoverPageHeaderMeta';
import normalizeQuery from './helpers/normalizeQuery';
import { useDiscoverPageCanonicalUrl } from './hooks/useDiscoverPageCanonicalUrl';

const DEFAULT_PAGE_SIZE = 24;

type RouteParams = {
    topic: string;
};

interface QueryParams {
    page?: number;
    pageSize?: number;
    status?: string;
}

const DiscoverPage = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const formatMessage = useAppFormatMessage();
    const { pathname, search } = location;
    const { topic } = useParams<RouteParams>();
    const canonicalUrl = useDiscoverPageCanonicalUrl();
    const itemIds = useAppSelector(getDiscoverItemIds);
    const totalRecords = useAppSelector(getDiscoverPaginationRecordsCount);
    const topicString = topic.split('-').join(' ');
    const queryParams: QueryParams = parseQueryParams(search);
    const { pageSize } = queryParams;
    const searchQuery = buildSearchQuery({
        pathname,
        queryParams: { ...queryParams, keyword: topicString },
        searchPageSize: pageSize ? Number(pageSize) : 24,
    });

    const { status = 'upcoming' } = queryParams;

    const navigateTo = useCallback(
        (param: QueryParams) => {
            const newParams = normalizeQuery({ ...queryParams, ...param });
            navigate({ search: toQueryString(newParams) });
        },
        [navigate, queryParams]
    );

    const handleTabChange = useCallback(
        (name: string) => {
            navigateTo({ status: name === 'archive' ? 'archive' : '' });
        },
        [navigateTo]
    );

    const { distance, publishDate, ranges } = searchQuery;
    const saveSearchParams = getSaveSearchParams({ keyword: topicString });
    const allSearchParams: SearchParamsType = {
        ...saveSearchParams,
        distance,
        publishDate,
        ranges,
    };

    const content = (
        <>
            <DiscoverPageHeader />
            <FluidItemGrid
                itemCardOption={CategorySearchCard}
                itemIds={itemIds}
            />
            {totalRecords > DEFAULT_PAGE_SIZE && (
                <Pagination
                    canonicalUrl={canonicalUrl}
                    onChange={navigateTo}
                    pageSizeOptions={PAGE_ITEMS_TO_VIEW}
                    paginationFilter={{
                        page: queryParams.page || 1,
                        pageSize: queryParams.pageSize || DEFAULT_PAGE_SIZE,
                        totalRecords,
                    }}
                />
            )}
        </>
    );

    return (
        <div className="mx-auto w-full max-w-[1240px] px-[20px] pb-[30px]">
            <DiscoverPageHeaderMeta
                firstItemId={itemIds?.[0]}
                title={topicString}
            />
            <PageTitle
                title={
                    <div className="flex">
                        {safelyRender({
                            html: capitalize(topicString),
                            wrapper: 'span',
                        })}
                        <div className="ml-[20px] flex min-h-[28px] min-w-[180px] flex-col items-center justify-center">
                            <SaveSearchContainer
                                allSearchParams={allSearchParams}
                                saveSearchParams={saveSearchParams}
                                small
                            />
                        </div>
                    </div>
                }
            />
            <Tabs
                className="[&_.hui-tab-header-row]:justify-end tabletMax:[&_.hui-tab-header-row]:justify-start"
                currentTab={status}
                onTabChanged={handleTabChange}
                tabs={[
                    {
                        content,
                        contentId: 'upcoming',
                        label: formatMessage(['upcoming']),
                    },
                    {
                        content,
                        contentId: 'archive',
                        label: formatMessage(['price_results']),
                    },
                ]}
            />
        </div>
    );
};

export default DiscoverPage;
